/** Visual policy editor state posted to the live PostgreSQL policy APIs. */
export type PolicyFormState = {
  name: string;
  command: string;
  roles: string;
  using?: string;
  check?: string;
  permissive?: boolean;
};

export type PolicyCommand = "ALL" | "SELECT" | "INSERT" | "UPDATE" | "DELETE";

export const POLICY_COMMANDS: PolicyCommand[] = [
  "SELECT",
  "INSERT",
  "UPDATE",
  "DELETE",
  "ALL",
];

export function policyCommandAllowsUsing(command: string): boolean {
  const c = (command || "ALL").toUpperCase();
  return c !== "INSERT";
}

export function policyCommandAllowsCheck(command: string): boolean {
  const c = (command || "ALL").toUpperCase();
  return c !== "SELECT" && c !== "DELETE";
}

/** Builds the JSON body for POST/PUT .../permissions/{table}/policies. */
export function buildPolicyBody(s: PolicyFormState): Record<string, unknown> {
  const roles = s.roles
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  const command = (s.command || "ALL").toUpperCase();
  const body: Record<string, unknown> = {
    name: s.name.trim(),
    command,
    permissive: s.permissive !== false,
  };
  if (roles.length > 0) body.roles = roles;
  const using = s.using?.trim();
  const check = s.check?.trim();
  // PostgreSQL rejects WITH CHECK on SELECT/DELETE and USING on INSERT.
  if (using && policyCommandAllowsUsing(command)) body.using = using;
  if (check && policyCommandAllowsCheck(command)) body.check = check;
  return body;
}

export function policyPresetExpression(
  kind: "owner" | "authenticated" | "public" | "deny",
  ownerColumn = "user_id",
): { using: string; check: string } {
  switch (kind) {
    case "public":
      return { using: "true", check: "true" };
    case "authenticated":
      return {
        using: "auth.role() = 'authenticated'",
        check: "auth.role() = 'authenticated'",
      };
    case "deny":
      return { using: "false", check: "false" };
    default:
      return {
        using: `${ownerColumn} = auth.uid()`,
        check: `auth.role() = 'authenticated' AND ${ownerColumn} = auth.uid()`,
      };
  }
}
